import { FormField, Textarea, Button } from '@amzn/awsui-components-react';
import { LoadingBar } from '@amzn/awsui-chat-components-console';
import { useTranslation } from 'react-i18next';
import { getCharLimitProps, FIELD_LIMITS } from '../../../../utils/fieldConstraints';
import './writingAssistant.scss';

interface SummarizedDescriptionFieldProps {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  aiEnabled: boolean;
  isGenerating: boolean;
  canGenerate: boolean;
  generateDisabledReason?: string;
  hasGenerationError: boolean;
  onGenerate: () => void;
  isAiGenerated: boolean;
  onUserEdit: () => void;
  errorText?: string;
  readOnly?: boolean;
}

export function SummarizedDescriptionField({
  value,
  onChange,
  onBlur,
  aiEnabled,
  isGenerating,
  canGenerate,
  generateDisabledReason,
  hasGenerationError,
  onGenerate,
  isAiGenerated,
  onUserEdit,
  errorText,
  readOnly,
}: SummarizedDescriptionFieldProps) {
  const { t } = useTranslation(['events', 'translation']);

  const charProps = getCharLimitProps(
    value,
    FIELD_LIMITS.SUMMARIZED_DESCRIPTION,
    t,
    'eventDetailsStep.summarizedDescriptionConstraint'
  );

  const handleChange = (newValue: string) => {
    onChange(newValue);
    if (isAiGenerated) {
      onUserEdit();
    }
  };

  if (!aiEnabled) {
    return (
      <FormField
        label={t('eventDetailsStep.summarizedDescription')}
        description={t('eventDetailsStep.summarizedDescriptionSubText')}
        constraintText={charProps.constraintText}
        errorText={errorText ?? charProps.errorText}
      >
        <Textarea
          onChange={(event) => onChange(event.detail.value)}
          onBlur={onBlur}
          value={value}
          placeholder={t('eventDetailsStep.summarizedDescriptionPlaceholder')}
          readOnly={readOnly}
        />
      </FormField>
    );
  }

  return (
    <FormField
      label={t('eventDetailsStep.summarizedDescription')}
      description={
        <span>
          {t('eventDetailsStep.summarizedDescriptionSubText')}
          {isAiGenerated && !isGenerating && (
            <span className="summarized-description__ai-label" data-testid="ai-generated-label">
              {' '}
              <span aria-hidden="true">✦</span> {t('writingAssistant.generatedByAi')}
            </span>
          )}
        </span>
      }
      constraintText={
        <span className="summarized-description__sr-only">
          {hasGenerationError ? t('writingAssistant.generateError') : charProps.constraintText}
        </span>
      }
      errorText={errorText ?? charProps.errorText}
    >
      <div className="summarized-description" data-testid="summarized-description-field">
        {isGenerating ? (
          <div
            className="summarized-description__loading"
            data-testid="summarized-description-loading"
          >
            <Textarea
              value=""
              onChange={() => undefined}
              placeholder={t('writingAssistant.generatingShortDescription')}
              readOnly
            />
            <LoadingBar variant="gen-ai" />
          </div>
        ) : (
          <Textarea
            onChange={(event) => handleChange(event.detail.value)}
            onBlur={onBlur}
            value={value}
            placeholder={t('writingAssistant.summarizedDescriptionPlaceholder')}
            readOnly={readOnly}
          />
        )}
        <div className="summarized-description__footer">
          <span
            className={`summarized-description__constraint${hasGenerationError ? ' summarized-description__constraint--error' : ''}`}
            role={hasGenerationError ? 'status' : undefined}
          >
            {hasGenerationError ? t('writingAssistant.generateError') : charProps.constraintText}
          </span>
          {!readOnly && !isGenerating && (
            <Button
              variant="normal"
              iconName="refresh"
              disabled={!canGenerate}
              disabledReason={generateDisabledReason ?? t('writingAssistant.minCharsHintGenerate')}
              onClick={onGenerate}
              data-testid="generate-short-desc-btn"
            >
              {hasGenerationError
                ? t('writingAssistant.retry')
                : value
                  ? t('writingAssistant.generateAnother')
                  : t('writingAssistant.generate')}
            </Button>
          )}
        </div>
      </div>
    </FormField>
  );
}
